import { isWeb } from '@gluestack-ui/nativewind-utils/IsWeb';
import { useURL } from 'expo-linking';
import {
  useLocalSearchParams,
  useRouter,
  usePathname,
  useGlobalSearchParams,
  useSegments,
  Href,
} from 'expo-router';
import { useCallback, useMemo, useRef } from 'react';

import { ROUTES } from '@/constants/routes';
import { toHomeTabPath } from '@/util/homeTabPath';
import { REDIRECT_PATH_PARAM, sanitizeRedirectDestination } from '@/util/redirectDestination';
import { buildRouteQueryString, withRouteQuery } from '@/util/routeQuery';
import { extractAppPath } from '@/utils/linkHelpers';

export { REDIRECT_PATH_PARAM };

/**
 * Hook for managing redirect_path parameter across authentication pages.
 *
 * Used to preserve the original destination path when users are redirected to login,
 * allowing them to return to their intended page after successful authentication.
 *
 * Features:
 * - Retrieves redirect_path from current URL search params
 * - Provides helper to build login URL with redirect_path param
 * - Provides helper to append redirect_path to any navigation path
 * - Returns the decoded redirect path for use after successful auth
 *
 * The returned path is sanitized, so every consumer is safe by construction rather than
 * by remembering to check. `redirect_path` arrives in a URL anyone can craft, and several
 * consumers hand it straight to `router.replace`.
 */
export function useRedirectPath() {
  const localParams = useLocalSearchParams<{ redirect_path?: string }>();

  /*
   * Read in a layout, `useLocalSearchParams` is scoped to the layout's own route and
   * comes back empty for a param the router put on a child route — which on native is
   * where the auth gate leaves it. The global params are the fallback, and they are safe
   * to fall back to because `redirect_path` means the same thing at every level.
   */
  const globalParams = useGlobalSearchParams<{ redirect_path?: string }>();

  const { replace } = useRouter();

  const encodedRedirectPath = localParams.redirect_path ?? globalParams.redirect_path;

  const redirectPath = useMemo(
    () => sanitizeRedirectDestination(encodedRedirectPath),
    [encodedRedirectPath]
  );

  /**
   * Builds a login URL with the redirect_path parameter.
   * @param returnPath - The path to redirect to after login. If not provided, uses current redirect_path.
   */
  const getLoginPathWithRedirect = (returnPath?: string): Href => {
    const path = returnPath || redirectPath;

    if (!path) {
      return ROUTES.LOGIN as Href;
    }

    return `${ROUTES.LOGIN}?${REDIRECT_PATH_PARAM}=${encodeURIComponent(path)}` as Href;
  };

  /**
   * Appends the current redirect_path to a navigation path if one exists.
   * Used when navigating between auth pages (e.g., login -> forgot-password)
   * to preserve the original redirect destination.
   */
  const appendRedirectPath = (path: string): Href =>
    buildAuthPathWithRedirect(path, redirectPath) as Href;

  /**
   * Navigates to redirect_path if present, otherwise to the provided default path.
   * Uses router navigation for in-app redirects without page reload.
   */
  const navigateToRedirectPath = useCallback(
    (defaultPath: string) => {
      performRedirectNavigation(redirectPath, defaultPath, replace);
    },
    [redirectPath, replace]
  );

  return {
    redirectPath,
    getLoginPathWithRedirect,
    appendRedirectPath,
    navigateToRedirectPath,
    REDIRECT_PATH_PARAM,
  };
}

/**
 * Builds a path to an auth screen that carries the destination to resume afterwards.
 *
 * Every route into the auth flow goes through here so that carrying the destination is
 * the default rather than something each call site has to remember. Forgetting is
 * invisible — the screen still works, the user still signs in, and the only symptom is
 * landing somewhere unrelated afterwards, which reads as the link having been wrong.
 *
 * A missing or unusable destination yields the bare path, so a caller with nothing to
 * resume is not a special case.
 */
export function buildAuthPathWithRedirect(target: string, returnPath?: string | null): string {
  const safeDestination = sanitizeRedirectDestination(returnPath);

  if (!safeDestination) {
    return target;
  }

  const separator = target.includes('?') ? '&' : '?';

  return `${target}${separator}${REDIRECT_PATH_PARAM}=${encodeURIComponent(safeDestination)}`;
}

/**
 * Utility function to build a login path with redirect_path parameter.
 * Can be used outside of React components.
 */
export function buildLoginPathWithRedirect(returnPath: string): string {
  return buildAuthPathWithRedirect(ROUTES.LOGIN, returnPath);
}

/**
 * Utility function to build a signup path with redirect_path parameter.
 * Can be used outside of React components.
 */
export function buildSignupPathWithRedirect(returnPath: string): string {
  return buildAuthPathWithRedirect(ROUTES.CREATE_ACCOUNT, returnPath);
}

/**
 * Hook to get the current page path for redirect purposes.
 * Works on both web and native by using expo-router hooks.
 * Returns pathname + search params as a string.
 */
export function useCurrentPagePath(): string {
  const pathname = usePathname();

  const params = useGlobalSearchParams();

  const segments = useSegments();

  return useMemo(
    () => withRouteQuery(pathname, buildRouteQueryString(params, segments)),
    [pathname, params, segments]
  );
}

/**
 * Reads the address bar. Web only, and only meaningful before the router rewrites it.
 *
 * A bare root path is reported as nothing rather than as a destination: it names no
 * authenticated resource, so treating it as one would ask a visitor to sign in to reach
 * the page they are already looking at.
 */
function readAddressBar(): string | null {
  if (!isWeb || typeof window === 'undefined' || !window.location) {
    return null;
  }

  const addressBarPath = `${window.location.pathname}${window.location.search}`;

  return addressBarPath === '/' ? null : addressBarPath;
}

/**
 * The destination a visitor is actually asking for, held across the router losing it.
 *
 * `useCurrentPagePath` alone is not enough at an auth gate. A gate that renders `null`
 * while it decides tears down the navigator underneath it; the router then collapses to
 * the group root and rewrites the address bar to an arbitrary sibling route, carrying the
 * original route's params along for the ride. Read at the moment the gate acts, the path
 * is a confident wrong answer rather than no answer — and because the params survive, the
 * wrong answer still looks like a real destination.
 *
 * So the path is only trusted while the router has actually resolved one, and the last
 * such value is held for the moment it stops. Holding rather than freezing keeps ordinary
 * navigation tracked, so a session that expires mid-use resumes the screen the user was on
 * rather than the one the app happened to open with.
 *
 * Resolving to a non-root path is not on its own enough to trust it. The route the router
 * rewrites to is a *sibling* of the one it lost, not the group root, so it arrives looking
 * resolved and overwrites a perfectly good destination with a gate screen — which the
 * destination policy then refuses, leaving nothing at all. Only a value that survives that
 * policy is held, which makes what is worth remembering and what is worth replaying one
 * question rather than two, and keeps a credential in the live query out of a ref that
 * outlives the navigation carrying it.
 *
 * The two fallbacks cover the gate acting before the router has resolved anything: the
 * address bar on web, and on native the link that launched the app, which is the case a
 * notification tap on a cold start lands in. They are held to the same policy, since a
 * cold start can land on a gate screen too.
 */
export function useEntryDestination(): string | null {
  const pathname = usePathname();

  const livePath = useCurrentPagePath();

  const launchUrl = useURL();

  const rememberedPath = useRef<string | null>(null);

  const routerHasResolved = !!pathname && pathname !== '/';

  if (routerHasResolved) {
    const resolvedDestination = sanitizeRedirectDestination(livePath);

    if (resolvedDestination) {
      rememberedPath.current = resolvedDestination;
    }
  } else if (rememberedPath.current === null) {
    rememberedPath.current = sanitizeRedirectDestination(
      readAddressBar() ?? (launchUrl ? extractAppPath(launchUrl) : null)
    );
  }

  return rememberedPath.current;
}

/**
 * Utility function to perform redirect navigation.
 * Uses router navigation for in-app redirects without page reload.
 * Can be used outside of the useRedirectPath hook when redirectPath comes from another source (e.g., sessionStorage).
 */
export function performRedirectNavigation(
  redirectPath: string | null,
  defaultPath: string,
  replace: (path: Href) => void
): void {
  /*
   * Re-validated here rather than trusted, because not every caller sources the path from
   * the hook: the OAuth callback reads it back from session storage, which same-origin
   * script can write. `defaultPath` is left alone — callers pass internal constants, and
   * some name an auth route the destination policy deliberately refuses.
   */
  const safeDestination = sanitizeRedirectDestination(redirectPath);

  replace((safeDestination ? toHomeTabPath(safeDestination) : defaultPath) as Href);
}
